import { cx } from '@linaria/core';
import { fontMedium } from 'styles/typography';
import { Popover, PopoverAction, usePopoverState } from 'components/Popover';
import { Link } from 'components/Link';
import { IconLinkExternal, IconDollar, IconQuestion, IconChevronDown, IconChevronUp } from 'components/Icons';
import { baseTheme } from 'styles/utils';
import { ModelPrice } from 'api/resource/fetchResourcePrices';
import { formatDecimalPrecision, formatPricingPerMillionToken, formatPricingToDollars } from 'utils/pricing';
import { useRef } from 'react';
import { container, descriptionLinkStyled, popoverStyle } from './styles';

type PricingChipProps = {
  children: React.ReactNode;
  border?: boolean;
  isPopoverOpen?: boolean;
  withChevronIcon?: boolean;
  className?: string;
};

type PricingChipWithPopoverProps = {
  children: React.ReactNode;
  border?: boolean;
  isTokenPricing?: boolean;
  popoverContent?: React.ReactNode;
  withChevronIcon?: boolean;
  pricingChipClassName?: string;
};

type FormattedModelPrice = {
  value: string;
  suffix: string;
};

type RawFormattedModelPrice = [] | [FormattedModelPrice] | [FormattedModelPrice, FormattedModelPrice];

export function formatModelPricing(modelPrice?: ModelPrice, raw?: true): RawFormattedModelPrice;

export function formatModelPricing(modelPrice?: ModelPrice, raw?: false): string;

export function formatModelPricing(modelPrice?: ModelPrice, raw: boolean = false): RawFormattedModelPrice | string {
  let rawModelPrice: RawFormattedModelPrice = [];

  if (isTokenBasedModel(modelPrice)) {
    rawModelPrice = [
      {
        value: formatPricingPerMillionToken(modelPrice?.price_per_prompt_token),
        suffix: ' / 1M Input Tokens',
      },
      {
        value: formatPricingPerMillionToken(modelPrice?.price_per_completion_token),
        suffix: ' / 1M Output Tokens',
      },
    ];
  } else if (isOpsBasedModel(modelPrice)) {
    rawModelPrice = [
      {
        value: formatDecimalPrecision(formatPricingToDollars(modelPrice?.price_per_op)),
        suffix: ' / request',
      },
    ];
  }

  if (raw) {
    return rawModelPrice;
  }

  return rawModelPrice.map((price) => `$${price.value}${price.suffix}`).join(' - ');
}

export function isOpsBasedModel(modelPrice?: ModelPrice): boolean {
  return Number(modelPrice?.model?.billing_type) === 2 && !!modelPrice?.price_per_op;
}

export function isTokenBasedModel(modelPrice?: ModelPrice): boolean {
  return Number(modelPrice?.model?.billing_type) === 1 && !!modelPrice?.price_per_prompt_token && !!modelPrice?.price_per_completion_token;
}

export function isValidModelPrice(modelPrice?: ModelPrice): boolean {
  return Boolean(modelPrice?.model && (isOpsBasedModel(modelPrice) || isTokenBasedModel(modelPrice)));
}

export function hasAllOpsBasedModels(modelPrices?: ModelPrice[]): boolean {
  return Boolean(modelPrices?.length && modelPrices?.every(isOpsBasedModel));
}

export function hasAllTokenBasedModels(modelPrices?: ModelPrice[]): boolean {
  return Boolean(modelPrices?.length && modelPrices?.every(isTokenBasedModel));
}

export function consolidateModelPricesIntoModelPrice(modelPrices: ModelPrice[] = []): ModelPrice | null {
  let consolidatedModelPrice: ModelPrice | null = null;

  if (hasAllOpsBasedModels(modelPrices)) {
    consolidatedModelPrice = {
      model: modelPrices[0].model,
      price_per_op: modelPrices.reduce((total, modelPrice) => total + (modelPrice.price_per_op || 0), 0),
    };
  }

  if (hasAllTokenBasedModels(modelPrices)) {
    consolidatedModelPrice = {
      model: modelPrices[0].model,
      price_per_prompt_token: modelPrices.reduce((total, modelPrice) => total + (modelPrice.price_per_prompt_token || 0), 0),
      price_per_completion_token: modelPrices.reduce((total, modelPrice) => total + (modelPrice.price_per_completion_token || 0), 0),
    };
  }

  return consolidatedModelPrice;
}

const DefaultPopoverContent: React.FC<Pick<PricingChipWithPopoverProps, 'isTokenPricing'>> = ({ isTokenPricing }) => (
  <>
    {isTokenPricing && <p>Price per 1M token</p>}
    <Link data-testid="learn-more" className={descriptionLinkStyled} href="https://www.clarifai.com/pricing" target="_blank">
      Learn more about pricing
      <IconLinkExternal size={16} color={baseTheme.light.colors.blue} />
    </Link>
  </>
);

export const PricingChip: React.FC<PricingChipProps> = ({ children, border, isPopoverOpen, withChevronIcon, className }) => {
  const ChevronIcon = isPopoverOpen ? IconChevronUp : IconChevronDown;

  const rightIcon = withChevronIcon ? (
    <ChevronIcon data-testid="icon-chevron" size={16} color={baseTheme.light.colors.gray700} />
  ) : (
    <IconQuestion data-testid="icon-question" size={16} color={baseTheme.light.colors.gray700} />
  );

  return (
    <div data-testid="pricing-chip" className={cx('content', border && 'border', className)} data-ispopoveropen={isPopoverOpen}>
      <span className="circular-icon">
        <IconDollar data-testid="icon-dollar" color={baseTheme.light.colors.white} width="0.8rem" height="0.8rem" />
      </span>
      <span className={fontMedium} data-testid="pricing-content">
        {children}
      </span>
      {rightIcon}
    </div>
  );
};

export const PricingChipWithPopover: React.FC<PricingChipWithPopoverProps> = ({
  children,
  border,
  isTokenPricing,
  popoverContent,
  withChevronIcon,
  pricingChipClassName,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const popover = usePopoverState({ placement: 'bottom-end' });
  const showPopoverContent = popoverContent || <DefaultPopoverContent isTokenPricing={isTokenPricing} />;

  return (
    <div ref={containerRef} className={container}>
      <PopoverAction popover={popover} data-testid="pricing-popover-action" data-collapsed="false">
        <PricingChip border={border} isPopoverOpen={popover.visible} withChevronIcon={withChevronIcon} className={pricingChipClassName}>
          {children}
        </PricingChip>
      </PopoverAction>
      <Popover
        className={popoverStyle}
        style={{ minWidth: containerRef.current?.getBoundingClientRect().width }}
        data-testid="pricing-popover"
        tabIndex={0}
        aria-label="pricing-popover"
        popover={popover}
      >
        <div className="popover-content">{showPopoverContent}</div>
      </Popover>
    </div>
  );
};

export const PricingChipContent: React.FC<{ modelPrice?: ModelPrice }> = ({ modelPrice }) => {
  const rawFormattedModelPrice = formatModelPricing(modelPrice, true);

  if (!modelPrice || !rawFormattedModelPrice?.length) {
    return null;
  }

  if (rawFormattedModelPrice.length === 1) {
    return (
      <>
        <span className="resource-price">${rawFormattedModelPrice[0].value}</span>
        {rawFormattedModelPrice[0].suffix}
      </>
    );
  }
  return (
    <div className="resource-price-container">
      <p>
        <span className="resource-price">${rawFormattedModelPrice[0].value}</span>
        {rawFormattedModelPrice[0].suffix}
      </p>
      <p>
        <span className="resource-price">${rawFormattedModelPrice[1].value}</span>
        {rawFormattedModelPrice[1].suffix}
      </p>
    </div>
  );
};
